import ChevronLeft from '@heroicons/react/20/solid/ChevronLeftIcon.js';
import ChevronRight from '@heroicons/react/20/solid/ChevronRightIcon.js';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from '../../../shared/components/date-time';
import { TIMEZONE } from '../../../shared/constants';
import { useToday } from '../../../shared/hooks/use-today';
import { FORMAT_LLL } from '../constants/misc';

dayjs.extend(isoWeek);
dayjs.extend(timezone);

export function Calendar({ initialDate, DayComponent }) {
  const today = useToday();
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    (initialDate ? dayjs.tz(initialDate, TIMEZONE) : today).startOf('isoWeek'),
  );

  const days = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => currentWeekStart.add(i, 'day')),
    [currentWeekStart],
  );

  const navigationLabel = useMemo(
    () => (
      <>
        <DateTime t={days.at(0)} format={FORMAT_LLL} />
        <span className="p-2">-</span>
        <DateTime t={days.at(-1)} format={FORMAT_LLL} />
      </>
    ),
    [days],
  );

  const handlePrev = () => setCurrentWeekStart((cw) => cw.subtract(7, 'day'));
  const handleNext = () => setCurrentWeekStart((cw) => cw.add(7, 'day'));

  return (
    <div className="relative z-1 -mx-5 sm:mx-0 mb-4.5 last:mb-0 pt-2 pb-4 before:absolute before:-z-1 before:top-0 before:-left-[9999px] before:bottom-0 before:-right-[9999px] before:bg-gray-50 lg:bg-gray-50 lg:before:hidden">
      <CalendarNavigation onPrev={handlePrev} onNext={handleNext} label={navigationLabel} />
      <div className="grid grid-cols-7 gap-x-2 px-2">
        {days.map((day) => (
          <DayComponent key={day.toISOString()} day={day} />
        ))}
      </div>
    </div>
  );
}

function CalendarNavigation({ label, isPrevDisabled, isNextDisabled, onPrev, onNext }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center gap-2 mb-2.5 px-3.5">
      <button
        type="button"
        disabled={isPrevDisabled}
        onClick={onPrev}
        className="text-primary hover:text-primary-hover disabled:text-gray-400"
        aria-label={t('prevWeek')}
      >
        <ChevronLeft className="size-5" aria-hidden="true" />
      </button>
      <div className="font-medium text-body-dark">{label}</div>
      <button
        type="button"
        disabled={isNextDisabled}
        onClick={onNext}
        className="text-primary hover:text-primary-hover disabled:text-gray-400"
        aria-label={t('nextWeek')}
      >
        <ChevronRight className="size-5" aria-hidden={true} />
      </button>
    </div>
  );
}
